import React, { useState, useEffect } from 'react';
import {
  FaCalendarAlt,
  FaSync,
  FaCheckCircle,
  FaExclamationCircle,
  FaPlus,
  FaArrowLeft,
  FaTrash,
  FaShoppingCart,
  FaListAlt,
  FaSearch,
  FaEye,
  FaReceipt,
  FaTimes,
  FaShoppingBag
} from 'react-icons/fa';
import { config } from '../utils/config';
import { safeStorage } from '../utils/storageHelper';
import '../css/admin/DeliveryFleet.css';

const FleetDataEntry = ({ dateFrom: propFrom, dateTo: propTo, hideHeader = false, onOrderCreated, onViewModeChange }) => {
  const [dateRange, setDateRange] = useState(() => {
    if (propFrom && propTo) {
      return { from: propFrom, to: propTo };
    }
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const format = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: format(firstDay), to: format(today) };
  });

  useEffect(() => {
    if (propFrom && propTo) {
      setDateRange({ from: propFrom, to: propTo });
    }
  }, [propFrom, propTo]);
  const [loading, setLoading] = useState(true);
  const [riders, setRiders] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [entries, setEntries] = useState({});
  const [message, setMessage] = useState(null);

  // View state: 'grid', 'addOrder', or 'manageOrders'
  const [viewMode, setViewMode] = useState('grid');

  // Counter Orders State
  const [counterOrders, setCounterOrders] = useState([]);
  const [counterLoading, setCounterLoading] = useState(false);
  const [counterError, setCounterError] = useState(null);
  const [counterPage, setCounterPage] = useState(1);
  const [counterTotalPages, setCounterTotalPages] = useState(1);
  const [counterTotalCount, setCounterTotalCount] = useState(0);
  const [counterStatusGroup, setCounterStatusGroup] = useState('ongoing');
  const [counterSearch, setCounterSearch] = useState('');
  const [selectedCounterOrder, setSelectedCounterOrder] = useState(null);
  const [counterDetailLoading, setCounterDetailLoading] = useState(false);

  // Manage Orders Sub-Tab State: 'counter' | 'online'
  const [manageTab, setManageTab] = useState('counter');

  // Online Orders States
  const [onlineOrders, setOnlineOrders] = useState([]);
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [onlineError, setOnlineError] = useState(null);
  const [onlineSearch, setOnlineSearch] = useState('');
  const [onlinePage, setOnlinePage] = useState(1);

  // Counter Order Status Update States
  const [statusUpdateOrder, setStatusUpdateOrder] = useState(null);
  const [newStatusValue, setNewStatusValue] = useState('delivered');
  const [selectedDeliveryPartner, setSelectedDeliveryPartner] = useState('');
  const [updatingCounterStatus, setUpdatingCounterStatus] = useState(false);
  const [updateStatusError, setUpdateStatusError] = useState(null);

  // Delivery Providers (Riders) State
  const [deliveryProviders, setDeliveryProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  const fetchDeliveryProviders = async () => {
    setLoadingProviders(true);
    try {
      const token = safeStorage.getItem('authToken') || localStorage.getItem('admin_token');
      const url = `${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/delivery-providers/?page=1&limit=100&status=Available`;
      const res = await fetch(url, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        const rawList = data.providers || data.results || data.data || (Array.isArray(data) ? data : []);
        // Filter strictly for Available partners
        const availableOnly = rawList.filter(p => {
          const st = String(p.status || p.provider_status || p.availability_status || '').toLowerCase();
          return !st || st === 'available' || p.is_available === true;
        });
        setDeliveryProviders(availableOnly);
      }
    } catch (err) {
      console.error('Error fetching available delivery providers:', err);
    } finally {
      setLoadingProviders(false);
    }
  };

  const getDeliveryPartnerDetails = (order) => {
    if (!order) return null;

    const name = order.delivery_partner_name ||
      order.delivery_partner?.name ||
      order.delivery_partner?.full_name ||
      order.delivery_partner_details?.delivery_partner_name ||
      order.delivery_partner_details?.name;

    if (name) {
      return name;
    }

    const partnerId = order.delivery_partner_id ||
      (typeof order.delivery_partner === 'object' ? order.delivery_partner?.id : order.delivery_partner) ||
      (order.delivery_partner_details && (order.delivery_partner_details.delivery_partner_id || order.delivery_partner_details.id));

    if (partnerId) {
      const foundInProviders = deliveryProviders.find(p => String(p.id || p.provider_id || p.delivery_partner_id || p.user_id) === String(partnerId));
      if (foundInProviders) {
        return foundInProviders.name || foundInProviders.provider_name || foundInProviders.full_name;
      }

      const foundInRiders = riders.find(r => String(r.id) === String(partnerId));
      if (foundInRiders) {
        return foundInRiders.name || foundInRiders.full_name;
      }

      return `Partner #${partnerId}`;
    }

    return null;
  };

  const formatOrderId = (id) => {
    if (!id && id !== 0) return '';
    const str = String(id).trim();
    const cleanStr = str.startsWith('#') ? str.slice(1) : str;
    if (cleanStr.startsWith('UNREC_')) {
      const parts = cleanStr.split('_');
      if (parts.length >= 3) {
        const formatted = `UNREC_${parts[parts.length - 1]}`;
        return str.startsWith('#') ? `#${formatted}` : formatted;
      }
    }
    return str;
  };

  const resolveDeliveryPartnerId = (order, providers = deliveryProviders, riderList = riders) => {
    if (!order) return '';

    const rawPartnerId = order.delivery_partner_id ||
      (typeof order.delivery_partner === 'object' ? (order.delivery_partner?.id || order.delivery_partner?.delivery_partner_id || order.delivery_partner?.user_id || order.delivery_partner?.provider_id) : order.delivery_partner) ||
      (order.delivery_partner_details && (order.delivery_partner_details.delivery_partner_id || order.delivery_partner_details.id || order.delivery_partner_details.user_id || order.delivery_partner_details.provider_id)) ||
      order.rider_id ||
      order.partner_id ||
      '';

    const partnerName = (
      order.delivery_partner_name ||
      order.delivery_partner?.name ||
      order.delivery_partner?.full_name ||
      order.delivery_partner_details?.delivery_partner_name ||
      order.delivery_partner_details?.name ||
      ''
    ).trim().toLowerCase();

    // 1. Match by ID in providers
    if (rawPartnerId) {
      const foundById = (providers || []).find(p =>
        String(p.id || '') === String(rawPartnerId) ||
        String(p.provider_id || '') === String(rawPartnerId) ||
        String(p.delivery_partner_id || '') === String(rawPartnerId) ||
        String(p.user_id || '') === String(rawPartnerId)
      );
      if (foundById) {
        return String(foundById.id || foundById.provider_id || foundById.delivery_partner_id || foundById.user_id || rawPartnerId);
      }
    }

    // 2. Match by Name in providers
    if (partnerName) {
      const foundByName = (providers || []).find(p => {
        const pName = (p.name || p.provider_name || p.full_name || '').trim().toLowerCase();
        return pName && (pName === partnerName || pName.includes(partnerName) || partnerName.includes(pName));
      });
      if (foundByName) {
        return String(foundByName.id || foundByName.provider_id || foundByName.delivery_partner_id || foundByName.user_id);
      }
    }

    // 3. Match in riders
    if (rawPartnerId) {
      const foundRider = (riderList || []).find(r => String(r.id || '') === String(rawPartnerId));
      if (foundRider) return String(foundRider.id);
    }
    if (partnerName) {
      const foundRiderByName = (riderList || []).find(r => {
        const rName = (r.name || r.full_name || '').trim().toLowerCase();
        return rName && (rName === partnerName || rName.includes(partnerName) || partnerName.includes(rName));
      });
      if (foundRiderByName) return String(foundRiderByName.id);
    }

    return rawPartnerId ? String(rawPartnerId) : (partnerName ? partnerName : '');
  };

  const renderDeliveryPartnerOptions = () => {
    const optionsMap = new Map();
    const seenNames = new Set();

    // 1. Add available deliveryProviders
    if (deliveryProviders.length > 0) {
      deliveryProviders.forEach(p => {
        const pId = String(p.id || p.provider_id || p.delivery_partner_id || p.user_id || '');
        if (!pId) return;
        const pName = (p.name || p.provider_name || p.full_name || `Partner #${pId}`).trim();
        const normName = pName.toLowerCase();
        if (!seenNames.has(normName) && !optionsMap.has(pId)) {
          optionsMap.set(pId, { id: pId, name: pName });
          seenNames.add(normName);
        }
      });
    } else {
      // 2. Fallback to riders only if deliveryProviders is empty
      riders.forEach(r => {
        const rId = String(r.id || '');
        if (!rId) return;
        const rName = (r.name || r.full_name || `Rider #${rId}`).trim();
        const normName = rName.toLowerCase();
        if (!seenNames.has(normName) && !optionsMap.has(rId)) {
          optionsMap.set(rId, { id: rId, name: rName });
          seenNames.add(normName);
        }
      });
    }

    // 3. Fallback only if the selected partner is not in options
    if (selectedDeliveryPartner && !optionsMap.has(String(selectedDeliveryPartner))) {
      const assignedDetails = statusUpdateOrder ? getDeliveryPartnerDetails(statusUpdateOrder) : null;
      const fallbackName = (assignedDetails && !assignedDetails.startsWith('Partner #')) ? assignedDetails : (isNaN(selectedDeliveryPartner) ? selectedDeliveryPartner : `Partner #${selectedDeliveryPartner}`);
      optionsMap.set(String(selectedDeliveryPartner), {
        id: String(selectedDeliveryPartner),
        name: fallbackName
      });
    }

    return Array.from(optionsMap.values()).map(opt => (
      <option key={opt.id} value={opt.id}>
        {opt.name}
      </option>
    ));
  };

  useEffect(() => {
    if (statusUpdateOrder) {
      const resolvedId = resolveDeliveryPartnerId(statusUpdateOrder, deliveryProviders, riders);
      if (resolvedId) {
        setSelectedDeliveryPartner(resolvedId);
      }
    }
  }, [statusUpdateOrder, deliveryProviders, riders]);

  useEffect(() => {
    if (onViewModeChange) {
      onViewModeChange(viewMode);
    }
  }, [viewMode, onViewModeChange]);

  const handleSaveCounterStatus = async () => {
    if (!statusUpdateOrder || !newStatusValue) return;
    if (newStatusValue === 'assigned' && !selectedDeliveryPartner) {
      setUpdateStatusError('Please select a delivery partner (rider) to assign this order.');
      return;
    }
    setUpdatingCounterStatus(true);
    setUpdateStatusError(null);
    try {
      const token = safeStorage.getItem('authToken') || localStorage.getItem('admin_token');
      const orderId = statusUpdateOrder.order_id || statusUpdateOrder.id;
      const orderSystem = statusUpdateOrder.order_system || (manageTab === 'counter' ? 'counter' : 'standard');
      const isUnrecorded = orderSystem === 'unrecorded' || Boolean(
        statusUpdateOrder.is_unrecorded ||
        statusUpdateOrder.unrecorded ||
        statusUpdateOrder.record_status === 'unrecorded' ||
        String(orderId || '').startsWith('UNREC_')
      );

      let res;
      if (newStatusValue === 'assigned') {
        const partnerId = selectedDeliveryPartner ? (isNaN(selectedDeliveryPartner) ? selectedDeliveryPartner : Number(selectedDeliveryPartner)) : null;
        const payload = {
          delivery_partner_id: partnerId,
          ...(isUnrecorded ? {} : { order_system: orderSystem }),
          new_status: newStatusValue,
          order_status: newStatusValue
        };
        res = await fetch(`${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/orders/${encodeURIComponent(orderId)}/assign-delivery/`, {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } else {
        const payload = {
          new_status: newStatusValue,
          order_status: newStatusValue,
          ...(isUnrecorded ? {} : { order_system: orderSystem })
        };
        res = await fetch(`${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/orders/${encodeURIComponent(orderId)}/status/`, {
          method: 'PATCH',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `HTTP error ${res.status}`);
      }
      setStatusUpdateOrder(null);
      if (manageTab === 'counter') {
        fetchCounterOrders();
      } else {
        fetchOnlineOrders();
      }
    } catch (err) {
      console.error('Update status error:', err);
      setUpdateStatusError(err.message || 'Failed to update order status');
    } finally {
      setUpdatingCounterStatus(false);
    }
  };

  const handleViewCounterOrder = async (order) => {
    const addrObj = (order.delivery_address && typeof order.delivery_address === 'object') ? order.delivery_address : {};
    const deliveryCustName = addrObj.name || addrObj.customer_name || addrObj.receiver_name || addrObj.recipient_name;
    const deliveryCustPhone = addrObj.mobile || addrObj.phone || addrObj.customer_phone || addrObj.receiver_phone || addrObj.contact_number;

    const initialCustName = deliveryCustName || order.customer_name || 'Walk-in';
    const initialCustPhone = deliveryCustPhone || order.customer_phone || order.mobileNumber || '';
    const initialOrderedByName = order.customer_name || '';
    const initialOrderedByPhone = order.customer_phone || order.mobileNumber || '';

    setSelectedCounterOrder({
      ...order,
      customer_name: initialCustName,
      customer_phone: initialCustPhone,
      ordered_by_name: initialOrderedByName,
      ordered_by_phone: initialOrderedByPhone
    });
    setCounterDetailLoading(true);
    try {
      const token = safeStorage.getItem('authToken') || localStorage.getItem('admin_token');
      const orderId = order.order_id || order.id;
      const orderSystem = order.order_system || (manageTab === 'counter' ? 'counter' : 'standard');
      const isUnrecorded = orderSystem === 'unrecorded' || Boolean(
        order.is_unrecorded ||
        order.unrecorded ||
        order.record_status === 'unrecorded' ||
        String(orderId || '').startsWith('UNREC_')
      );

      let url;
      if (orderSystem === 'counter') {
        url = `${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/orders/counter/${encodeURIComponent(orderId)}/`;
      } else if (isUnrecorded) {
        url = `${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/orders/unrecorded/${encodeURIComponent(orderId)}/`;
      } else {
        url = `${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/orders/${encodeURIComponent(orderId)}/?order_system=${encodeURIComponent(orderSystem)}`;
      }

      const res = await fetch(url, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        const fullData = data.data?.order || (data.data && !data.data.order ? data.data : null) || data.order || data;
        if (fullData) {
          const detailAddr = (fullData.delivery_address && typeof fullData.delivery_address === 'object') ? fullData.delivery_address : {};
          const detailDeliveryName = detailAddr.name || detailAddr.customer_name || detailAddr.receiver_name || detailAddr.recipient_name;
          const detailDeliveryPhone = detailAddr.mobile || detailAddr.phone || detailAddr.customer_phone || detailAddr.receiver_phone || detailAddr.contact_number;

          const effectiveCustName = detailDeliveryName || fullData.customer_name || (fullData.first_name ? `${fullData.first_name} ${fullData.last_name || ''}`.trim() : order.customer_name);
          const effectiveCustPhone = detailDeliveryPhone || fullData.customer_phone || fullData.phone_number || order.customer_phone || '';

          const orderedByName = fullData.customer_name || (fullData.first_name ? `${fullData.first_name} ${fullData.last_name || ''}`.trim() : '') || order.customer_name;
          const orderedByPhone = fullData.customer_phone || fullData.phone_number || order.customer_phone;

          setSelectedCounterOrder(prev => ({
            ...prev,
            ...fullData,
            customer_name: effectiveCustName,
            customer_phone: effectiveCustPhone,
            ordered_by_name: orderedByName,
            ordered_by_phone: orderedByPhone
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
    } finally {
      setCounterDetailLoading(false);
    }
  };

  const fetchCounterOrders = async () => {
    setCounterLoading(true);
    setCounterError(null);
    try {
      const token = safeStorage.getItem('authToken') || localStorage.getItem('admin_token');
      const query = new URLSearchParams({
        page: counterPage,
        limit: 10,
        status_group: counterStatusGroup,
        date_from: dateRange.from,
        date_to: dateRange.to,
        _t: Date.now()
      });
      if (counterSearch) query.append('search', counterSearch);

      const url = `${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/orders/counter/?${query.toString()}`;
      const res = await fetch(url, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const data = await res.json();
      if (data && (data.success !== false)) {
        const orderList = data.results || data.orders || [];
        setCounterOrders(orderList);
        setCounterTotalPages(data.pages || data.pagination?.total_pages || 1);
        setCounterTotalCount(data.count || data.pagination?.total_orders || orderList.length);
      } else {
        setCounterOrders([]);
      }
    } catch (err) {
      console.error('Fetch Counter Orders error:', err);
      setCounterError('Failed to load counter orders: ' + (err.message || 'Error occurred'));
      setCounterOrders([]);
    } finally {
      setCounterLoading(false);
    }
  };

  const fetchOnlineOrders = async () => {
    setOnlineLoading(true);
    setOnlineError(null);
    try {
      const token = safeStorage.getItem('authToken') || localStorage.getItem('admin_token');
      const query = new URLSearchParams({
        page: 1,
        limit: 200,
        date_from: dateRange.from,
        date_to: dateRange.to,
        _t: Date.now()
      });
      if (onlineSearch) query.append('search', onlineSearch);

      const url = `${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/orders/?${query.toString()}`;
      const res = await fetch(url, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const data = await res.json();
      if (data && (data.success !== false)) {
        const rawList = data.results || data.orders || data.data?.orders || data.data?.results || [];
        // Filter strictly for order_type === 'delivery' AND payment_status in ['success', 'paid']
        const filteredList = rawList.filter(o => {
          const ot = (o.order_type || '').toLowerCase().trim();
          const ps = (o.payment_status || '').toLowerCase().trim();
          return ot === 'delivery' && (ps === 'success' || ps === 'paid');
        });
        setOnlineOrders(filteredList);
      } else {
        setOnlineOrders([]);
      }
    } catch (err) {
      console.error('Fetch Online Orders error:', err);
      setOnlineError('Failed to load online orders: ' + (err.message || 'Error occurred'));
      setOnlineOrders([]);
    } finally {
      setOnlineLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'manageOrders') {
      if (manageTab === 'counter') {
        fetchCounterOrders();
      } else {
        fetchOnlineOrders();
      }
      fetchDeliveryProviders();
      if (riders.length === 0 && dateRange.from && dateRange.to) {
        fetchEntries(dateRange.from, dateRange.to);
      }
    }
  }, [viewMode, manageTab, counterPage, counterStatusGroup, dateRange.from, dateRange.to]);

  const getLoggedInUserId = () => {
    try {
      const savedUserStr = safeStorage.getItem('user') || safeStorage.getItem('kirazee_user');
      if (savedUserStr) {
        const savedUser = JSON.parse(savedUserStr);
        return savedUser?.user_id || savedUser?.id || savedUser?.staff_id || '';
      }
    } catch (e) {
      console.error('Error fetching logged in user id:', e);
    }
    return '';
  };

  // Add Order Form State
  const initialOrderForm = {
    business_id: '',
    order_type: 'delivery',
    // Customer details
    user_id: getLoggedInUserId(),
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    // Delivery details
    door_no: '',
    street: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    delivery_partner_id: '',
    // Financials & Instructions
    delivery_charges: '',
    discount_amount: '',
    parcel_charges: '',
    customization_charges: '',
    coupon_code: '',
    delivery_instruction: '',
    order_instruction: ''
  };

  const initialItem = {
    menu_item_id: null,
    product_item_id: null,
    variant_id: null,
    item_name_snapshot: '',
    quantity: 1,
    unit_price_snapshot: '',
    gst_percentage: '',
    gst_amount: '',
    total_price: '',
    item_details: null,
    tax_details: null,
    customizations: []
  };

  const [orderForm, setOrderForm] = useState(initialOrderForm);
  const [items, setItems] = useState([{ ...initialItem }]);
  const [businessItems, setBusinessItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [formError, setFormError] = useState(null);

  const fetchEntries = async (fromDate, toDate) => {
    setLoading(true);
    setMessage(null);
    try {
      const token = safeStorage.getItem('authToken');
      const res = await fetch(`${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/fleet/entries/?date_from=${fromDate}&date_to=${toDate}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const data = await res.json();
      setRiders(data.riders || []);
      setMerchants(data.merchants || []);

      const entryMap = {};
      (data.entries || []).forEach(e => {
        const attVal = e.attendance ? e.attendance : (e.is_available ? 'P' : 'A');
        entryMap[e.riderId] = {
          attendance: attVal,
          is_available: e.is_available !== undefined ? e.is_available : attVal === 'P',
          orders: e.orders ?? 0,
          km: e.km !== null ? e.km : '',
          otHours: e.otHours !== null ? e.otHours : '',
          merchantOrders: e.merchantOrders || {},
          payoutMod: e.payoutMod || false,
          notes: e.notes || '',
        };
      });
      setEntries(entryMap);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
      setMessage({ type: 'error', text: `Failed to load data: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      fetchEntries(dateRange.from, dateRange.to);
    }
  }, [dateRange.from, dateRange.to]);

  // Fetch products/items for the selected business strictly via admin products API
  useEffect(() => {
    if (!orderForm.business_id) {
      setBusinessItems([]);
      return;
    }

    const fetchBusinessItems = async () => {
      setLoadingItems(true);
      try {
        const token = safeStorage.getItem('authToken');
        const res = await fetch(`${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/businesses/${orderForm.business_id}/products/`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          }
        });
        const data = await res.json();
        let rawList = [];

        if (res.ok) {
          if (Array.isArray(data.data)) {
            rawList = data.data;
          } else if (Array.isArray(data.products)) {
            rawList = data.products;
          } else if (Array.isArray(data.items)) {
            rawList = data.items;
          } else if (Array.isArray(data.results)) {
            rawList = data.results;
          } else if (Array.isArray(data)) {
            rawList = data;
          }
        }

        const formatted = rawList.map(it => {
          const priceVal = parseFloat(it.selling_price || it.price || (it.variants && it.variants[0] && it.variants[0].selling_price) || it.final_price || it.unit_price || 0);
          const gstPct = parseFloat(
            it.gst_percentage ||
            it.gst ||
            (it.variants && it.variants[0] && it.variants[0].gst_percentage) ||
            (it.tax_details && it.tax_details.gst_percentage) ||
            5.0
          );
          const gstId = parseInt(
            it.gst_id ||
            it.tax_id ||
            (it.variants && it.variants[0] && it.variants[0].gst_id) ||
            (it.tax_details && it.tax_details.tax_id) ||
            2
          );
          const variantId = it.variant_id || (it.variants && it.variants[0] && it.variants[0].variant_id) || null;
          const categoryName = it.category || it.category_name || null;
          const itemType = it.type || it.food_type || 'veg';
          const description = it.description || null;
          const originalCost = parseFloat(it.original_cost || it.mrp || (it.variants && it.variants[0] && it.variants[0].mrp) || priceVal);

          return {
            id: it.item_id || it.id || it.product_id,
            name: it.item_name || it.name || it.product_name || `Item #${it.item_id || it.id}`,
            price: priceVal,
            gst_percentage: gstPct,
            gst_id: gstId,
            variant_id: variantId,
            category: categoryName,
            type: itemType,
            description: description,
            original_cost: originalCost,
            menu_item_id: it.item_id || it.id || null,
            product_item_id: it.product_id || null
          };
        });

        setBusinessItems(formatted);
      } catch (err) {
        console.error('Failed to fetch business items:', err);
        setBusinessItems([]);
      } finally {
        setLoadingItems(false);
      }
    };

    fetchBusinessItems();
  }, [orderForm.business_id]);

  // Handle Business Selection
  const handleBusinessSelect = (businessId) => {
    setOrderForm(prev => ({ ...prev, business_id: businessId }));
    // Reset items when business changes
    setItems([{ ...initialItem }]);
  };

  // Item handlers
  const handleSelectItemName = (index, selectedItemName) => {
    const updatedItems = [...items];
    const foundProduct = businessItems.find(p => p.name === selectedItemName);

    if (foundProduct) {
      const qty = parseInt(updatedItems[index].quantity) || 1;
      const unitPrice = parseFloat(foundProduct.price) || 0;
      const gstPct = parseFloat(foundProduct.gst_percentage) || 0;
      const itemSubtotal = unitPrice * qty;
      const gstAmt = parseFloat(((itemSubtotal * gstPct) / 100).toFixed(2));
      const cgst = parseFloat((gstAmt / 2).toFixed(2));
      const sgst = parseFloat((gstAmt - cgst).toFixed(2));
      const totalPrice = parseFloat((itemSubtotal + gstAmt).toFixed(2));

      updatedItems[index] = {
        ...updatedItems[index],
        menu_item_id: foundProduct.menu_item_id,
        product_item_id: foundProduct.product_item_id,
        variant_id: foundProduct.variant_id,
        item_name_snapshot: foundProduct.name,
        quantity: qty,
        unit_price_snapshot: unitPrice,
        gst_percentage: gstPct,
        gst_amount: gstAmt,
        total_price: totalPrice,
        item_details: {
          description: foundProduct.description,
          category: foundProduct.category,
          type: foundProduct.type,
          gst_id: foundProduct.gst_id,
          gst_percentage: gstPct,
          gst_amount: gstAmt,
          original_cost: foundProduct.original_cost,
          selling_price: unitPrice
        },
        tax_details: {
          tax_id: foundProduct.gst_id,
          tax_name: 'GST',
          cgst_amount: cgst,
          sgst_amount: sgst,
          igst_amount: 0.0,
          ugst_amount: 0.0,
          total_gst: gstAmt
        },
        customizations: []
      };
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        item_name_snapshot: selectedItemName,
      };
    }
    setItems(updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;

    if (field === 'quantity') {
      const qty = parseInt(value) || 1;
      const unitPrice = parseFloat(updatedItems[index].unit_price_snapshot) || 0;
      const gstPct = parseFloat(updatedItems[index].gst_percentage) || 0;
      const itemSubtotal = unitPrice * qty;
      const gstAmt = parseFloat(((itemSubtotal * gstPct) / 100).toFixed(2));
      const cgst = parseFloat((gstAmt / 2).toFixed(2));
      const sgst = parseFloat((gstAmt - cgst).toFixed(2));
      const totalPrice = parseFloat((itemSubtotal + gstAmt).toFixed(2));

      updatedItems[index].quantity = qty;
      updatedItems[index].gst_amount = gstAmt;
      updatedItems[index].total_price = totalPrice;

      if (updatedItems[index].item_details) {
        updatedItems[index].item_details.gst_amount = gstAmt;
      }
      if (updatedItems[index].tax_details) {
        updatedItems[index].tax_details.cgst_amount = cgst;
        updatedItems[index].tax_details.sgst_amount = sgst;
        updatedItems[index].tax_details.total_gst = gstAmt;
      }
    }

    setItems(updatedItems);
  };

  const handleAddItem = () => {
    setItems(prev => [...prev, { ...initialItem }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // Submit Order to Backend API
  const handleCreateOrderSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!orderForm.business_id) {
      setFormError('Please select a Business.');
      return;
    }

    const validItems = items.filter(it => it.item_name_snapshot && it.item_name_snapshot.trim() !== '');
    if (validItems.length === 0) {
      setFormError('Please select at least one item from the Item Name dropdown.');
      return;
    }

    for (let i = 0; i < validItems.length; i++) {
      if (validItems[i].unit_price_snapshot === '' || parseFloat(validItems[i].unit_price_snapshot) < 0) {
        setFormError(`Please enter a valid Unit Price for item #${i + 1} ("${validItems[i].item_name_snapshot}")`);
        return;
      }
    }

    setSubmittingOrder(true);
    try {
      let itemsBaseTotal = 0;
      let itemsTotal = 0;
      let totalGst = 0;
      let totalCgst = 0;
      let totalSgst = 0;

      const formattedItems = validItems.map(it => {
        const qty = parseInt(it.quantity) || 1;
        const baseUnitPrice = parseFloat(it.unit_price_snapshot) || 0;
        const unitPrice = baseUnitPrice;
        const gstPct = parseFloat(it.gst_percentage) || 0;

        const itemSubtotal = baseUnitPrice * qty;
        const gstAmt = parseFloat(((itemSubtotal * gstPct) / 100).toFixed(2));
        const cgstAmt = parseFloat((gstAmt / 2).toFixed(2));
        const sgstAmt = parseFloat((gstAmt - cgstAmt).toFixed(2));
        const totalPrice = parseFloat((itemSubtotal + gstAmt).toFixed(2));

        itemsBaseTotal += itemSubtotal;
        itemsTotal += itemSubtotal;
        totalGst += gstAmt;
        totalCgst += cgstAmt;
        totalSgst += sgstAmt;

        const itemDetails = it.item_details || {
          description: null,
          category: null,
          type: 'veg',
          gst_id: 2,
          gst_percentage: gstPct,
          gst_amount: gstAmt,
          original_cost: unitPrice,
          selling_price: unitPrice
        };

        const taxDetails = it.tax_details || {
          tax_id: 2,
          tax_name: 'GST',
          cgst_amount: cgstAmt,
          sgst_amount: sgstAmt,
          igst_amount: 0.0,
          ugst_amount: 0.0,
          total_gst: gstAmt
        };

        return {
          menu_item_id: it.menu_item_id || null,
          product_item_id: it.product_item_id || null,
          variant_id: it.variant_id || null,
          item_name: it.item_name_snapshot,
          item_name_snapshot: it.item_name_snapshot,
          quantity: qty,
          base_unit_price: baseUnitPrice,
          customization_extra_unit: 0.0,
          customization_extra_total: 0.0,
          unit_price: unitPrice,
          unit_price_snapshot: unitPrice,
          gst_percentage: gstPct,
          gst_amount: gstAmt,
          total_price: totalPrice,
          item_details: itemDetails,
          tax_details: taxDetails,
          customizations: it.customizations || []
        };
      });

      const delCharges = parseFloat(orderForm.delivery_charges) || 0;
      const parcCharges = parseFloat(orderForm.parcel_charges) || 0;
      const custCharges = parseFloat(orderForm.customization_charges || orderForm.platform_fee) || 0;
      const discAmount = parseFloat(orderForm.discount_amount) || 0;

      const subtotal = itemsTotal + totalGst;
      const afterCoupon = subtotal - discAmount;
      const finalAmount = Math.round(afterCoupon + delCharges + parcCharges + custCharges);

      let deliveryAddressSnapshot = null;
      if (orderForm.order_type === 'delivery') {
        const fullAddrParts = [
          orderForm.door_no ? `Door No: ${orderForm.door_no}` : '',
          orderForm.street || '',
          orderForm.landmark ? `Near ${orderForm.landmark}` : '',
          orderForm.city || '',
          orderForm.state || '',
          orderForm.pincode ? `Pincode: ${orderForm.pincode}` : ''
        ].filter(Boolean);

        deliveryAddressSnapshot = {
          name: orderForm.customer_name || null,
          mobile: orderForm.customer_phone || null,
          email: orderForm.customer_email || null,
          door_no: orderForm.door_no || null,
          street: orderForm.street || null,
          landmark: orderForm.landmark || null,
          city: orderForm.city || null,
          state: orderForm.state || null,
          pincode: orderForm.pincode || null,
          address: fullAddrParts.join(', '),
          instructions: orderForm.delivery_instruction || null
        };
      } else if (orderForm.customer_name || orderForm.customer_phone || orderForm.customer_email) {
        deliveryAddressSnapshot = {
          name: orderForm.customer_name || null,
          mobile: orderForm.customer_phone || null,
          email: orderForm.customer_email || null,
          door_no: null,
          street: null,
          landmark: null,
          city: null,
          state: null,
          pincode: null,
          address: null,
          instructions: null
        };
      }

      const token = safeStorage.getItem('authToken');
      const payload = {
        business_id: orderForm.business_id,
        user_id: orderForm.user_id || getLoggedInUserId() || null,
        order_type: orderForm.order_type,
        status: 'pending',
        payment_status: null,
        payment_method: null,
        customer_name: orderForm.customer_name || null,
        customer_phone: orderForm.customer_phone || null,
        customer_email: orderForm.customer_email || null,
        customer_details: {
          name: orderForm.customer_name || null,
          phone: orderForm.customer_phone || null,
          mobile: orderForm.customer_phone || null,
          email: orderForm.customer_email || null
        },
        delivery_partner_id: orderForm.delivery_partner_id || null,
        delivery_address_snapshot: deliveryAddressSnapshot,
        delivery_charges: delCharges,
        discount_amount: discAmount,
        parcel_charges: parcCharges,
        customization_charges: custCharges,
        customizations_total: custCharges,
        coupon_code: orderForm.coupon_code || null,
        delivery_instruction: orderForm.delivery_instruction || null,
        order_instruction: orderForm.order_instruction || null,
        order_summary: {
          parcel_charges: parcCharges,
          items_total: parseFloat(itemsTotal.toFixed(2)),
          items_base_total: parseFloat(itemsBaseTotal.toFixed(2)),
          customizations_total: custCharges,
          delivery_charges: delCharges,
          total_gst: parseFloat(totalGst.toFixed(2)),
          tax_breakdown: {
            cgst_amount: parseFloat(totalCgst.toFixed(2)),
            sgst_amount: parseFloat(totalSgst.toFixed(2)),
            igst_amount: 0.0,
            ugst_amount: 0.0,
            total_gst: parseFloat(totalGst.toFixed(2)),
            jurisdiction: 'INTRA_STATE'
          },
          subtotal: parseFloat(subtotal.toFixed(2)),
          coupon_discount: discAmount,
          after_coupon: parseFloat(afterCoupon.toFixed(2)),
          wallet_points_used: 0.0,
          wallet_points_value: 0.0,
          final_amount: parseFloat(finalAmount.toFixed(2))
        },
        items: formattedItems
      };

      const res = await fetch(`${config.API_BASE_URL}${config.API_PREFIX}/api/v1/admin/orders/create/`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to create order');
      }

      // Success
      const orderInfo = data.data || data.order;
      setMessage({ type: 'success', text: `Order ${orderInfo?.order_number || ''} created successfully!` });
      setOrderForm(initialOrderForm);
      setItems([{ ...initialItem }]);
      setViewMode('grid');
      fetchEntries(dateRange.from, dateRange.to);
      if (onOrderCreated) {
        onOrderCreated();
      }
    } catch (err) {
      console.error('Error creating order:', err);
      setFormError(err.message || 'An error occurred while creating order');
    } finally {
      setSubmittingOrder(false);
    }
  };

  return (
    <div style={{ padding: hideHeader ? '0' : '24px' }}>
      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
            {viewMode === 'grid'
              ? 'Fleet Operational Data Grid'
              : viewMode === 'addOrder'
                ? 'Add Manual Order'
                : 'Business Counter Orders (POS)'}
          </h2>
          {!hideHeader && (
            <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: '14px' }}>
              {viewMode === 'grid'
                ? 'Daily attendance, orders, KM distance, OT hours, and merchant attribution log'
                : viewMode === 'addOrder'
                  ? 'Create manual order entry stored directly in orders and order_items tables'
                  : 'View and manage in-store counter orders recorded for businesses'}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {viewMode === 'grid' ? (
            <>
              <button
                onClick={() => { setFormError(null); setViewMode('addOrder'); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#F55D00',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '9px 16px',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(245, 93, 0, 0.2)'
                }}
              >
                <FaPlus /> Add Order
              </button>

              <button
                onClick={() => { setCounterError(null); setViewMode('manageOrders'); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#2A2C41',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '9px 16px',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(42, 44, 65, 0.2)'
                }}
              >
                <FaListAlt /> Manage Orders
              </button>

              {!hideHeader && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '6px 12px' }}>
                  <FaCalendarAlt style={{ color: '#64748b' }} />
                  <input
                    type="date"
                    title="Start Date"
                    value={dateRange.from}
                    onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                    style={{ border: 'none', outline: 'none', background: 'transparent', fontWeight: '600', color: '#334155' }}
                  />
                  <span style={{ color: '#64748b', fontWeight: '500', fontSize: '14px' }}>to</span>
                  <input
                    type="date"
                    title="End Date"
                    value={dateRange.to}
                    onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                    style={{ border: 'none', outline: 'none', background: 'transparent', fontWeight: '600', color: '#334155' }}
                  />
                </div>
              )}
            </>
          ) : (
            <button
              onClick={() => setViewMode('grid')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#f1f5f9',
                color: '#334155',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                padding: '8px 16px',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <FaArrowLeft /> Back to Fleet Grid
            </button>
          )}
        </div>
      </div>

      {message && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          color: message.type === 'success' ? '#166534' : '#991b1b',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {message.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
          {message.text}
        </div>
      )}

      {/* VIEW MODE 1: ADD ORDER SELECTION PAGE */}
      {viewMode === 'addOrder' && (
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '28px', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ background: '#fff7ed', color: '#F55D00', padding: '10px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaShoppingCart style={{ fontSize: '20px' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Add Manual Order Entry</h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' }}>Select business and items to record order data into `orders` and `order_items` tables</p>
            </div>
          </div>

          {formError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              <FaExclamationCircle /> {formError}
            </div>
          )}

          <form onSubmit={handleCreateOrderSubmit}>
            {/* Section 1: Order & Merchant Info */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#334155', marginBottom: '14px', borderBottom: '2px solid #f1f5f9', paddingBottom: '6px' }}>
                Order & Merchant Info
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    Business / Merchant <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    value={orderForm.business_id}
                    onChange={(e) => handleBusinessSelect(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
                    required
                  >
                    <option value="">Select Merchant...</option>
                    {merchants.map(m => (
                      <option key={m.id} value={m.id}>{m.business_name || m.name || m.id} ({m.id})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Order Type</label>
                  <select
                    value={orderForm.order_type}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, order_type: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
                  >
                    <option value="delivery">Delivery</option>
                    <option value="pickup">Pickup</option>
                    <option value="dine_in">Dine In</option>
                    <option value="takeaway">Takeaway</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Section 2: Customer Details */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#334155', marginBottom: '14px', borderBottom: '2px solid #f1f5f9', paddingBottom: '6px' }}>
                Customer Details
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Customer Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Rahul Sharma"
                    value={orderForm.customer_name}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, customer_name: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Customer Mobile / Phone</label>
                  <input
                    type="tel"
                    placeholder="e.g. 9876543210"
                    value={orderForm.customer_phone}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>


                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Customer Email (Optional)</label>
                  <input
                    type="email"
                    placeholder="e.g. rahul@example.com"
                    value={orderForm.customer_email}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, customer_email: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Delivery Details (Shown ONLY when Order Type is Delivery) */}
            {orderForm.order_type === 'delivery' && (
              <div style={{ marginBottom: '24px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginTop: 0, marginBottom: '14px', borderBottom: '2px solid #e2e8f0', paddingBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Delivery Details <span style={{ fontSize: '12px', fontWeight: '500', color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: '12px' }}>Required for Delivery Orders</span>
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Door / Flat / House No</label>
                    <input
                      type="text"
                      placeholder="e.g. Flat 302, Sunrise Apts"
                      value={orderForm.door_no}
                      onChange={(e) => setOrderForm(prev => ({ ...prev, door_no: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Street / Road / Area</label>
                    <input
                      type="text"
                      placeholder="e.g. MG Road, Indiranagar"
                      value={orderForm.street}
                      onChange={(e) => setOrderForm(prev => ({ ...prev, street: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Landmark</label>
                    <input
                      type="text"
                      placeholder="e.g. Near Metro Station"
                      value={orderForm.landmark}
                      onChange={(e) => setOrderForm(prev => ({ ...prev, landmark: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>City / Town</label>
                    <input
                      type="text"
                      placeholder="e.g. Bengaluru"
                      value={orderForm.city}
                      onChange={(e) => setOrderForm(prev => ({ ...prev, city: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>State</label>
                    <input
                      type="text"
                      placeholder="e.g. Karnataka"
                      value={orderForm.state}
                      onChange={(e) => setOrderForm(prev => ({ ...prev, state: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Pincode</label>
                    <input
                      type="text"
                      placeholder="e.g. 560038"
                      value={orderForm.pincode}
                      onChange={(e) => setOrderForm(prev => ({ ...prev, pincode: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
                    />
                  </div>



                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Delivery Instruction</label>
                    <input
                      type="text"
                      placeholder="e.g. Ring bell, leave at gate"
                      value={orderForm.delivery_instruction}
                      onChange={(e) => setOrderForm(prev => ({ ...prev, delivery_instruction: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Section 4: Financial Charges */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#334155', marginBottom: '14px', borderBottom: '2px solid #f1f5f9', paddingBottom: '6px' }}>
                Charges & Adjustments (₹)
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Delivery Charges (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={orderForm.delivery_charges}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, delivery_charges: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Discount Amount (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={orderForm.discount_amount}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, discount_amount: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Parcel Charges (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={orderForm.parcel_charges}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, parcel_charges: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Customization Charges (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={orderForm.customization_charges}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, customization_charges: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Coupon Code</label>
                  <input
                    type="text"
                    placeholder="e.g. WELCOME50"
                    value={orderForm.coupon_code}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, coupon_code: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Order Items (Stored in order_items table) */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '2px solid #f1f5f9', paddingBottom: '6px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#334155', margin: 0 }}>
                  Order Items <span style={{ fontSize: '12px', fontWeight: '500', color: '#64748b' }}>(Stored in `order_items` table)</span>
                </h4>
                <button
                  type="button"
                  onClick={handleAddItem}
                  disabled={!orderForm.business_id || loadingItems}
                  style={{
                    background: (!orderForm.business_id || loadingItems) ? '#f1f5f9' : '#eff6ff',
                    color: (!orderForm.business_id || loadingItems) ? '#94a3b8' : '#2563eb',
                    border: '1px solid #bfdbfe',
                    borderRadius: '6px',
                    padding: '6px 14px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: (!orderForm.business_id || loadingItems) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <FaPlus /> Add Item
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                      <th style={{ padding: '10px 12px' }}>#</th>
                      <th style={{ padding: '10px 12px', minWidth: '240px' }}>Item Name<span style={{ color: '#ef4444' }}>*</span></th>
                      <th style={{ padding: '10px 12px', width: '100px' }}>Qty</th>
                      <th style={{ padding: '10px 12px', width: '130px' }}>Unit Price </th>
                      <th style={{ padding: '10px 12px', width: '130px' }}>GST Percentage</th>
                      <th style={{ padding: '10px 12px', width: '130px' }}>Total Price (₹)</th>
                      <th style={{ padding: '10px 12px', width: '60px', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const qty = parseInt(item.quantity) || 1;
                      const uPrice = parseFloat(item.unit_price_snapshot) || 0;
                      const gstPct = parseFloat(item.gst_percentage) || 0;
                      const itemSubtotal = qty * uPrice;
                      const gstAmt = parseFloat(((itemSubtotal * gstPct) / 100).toFixed(2));
                      const lineTotal = item.total_price ? parseFloat(item.total_price).toFixed(2) : (itemSubtotal + gstAmt).toFixed(2);

                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 12px', color: '#64748b', fontWeight: '600' }}>{idx + 1}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <select
                              value={item.item_name_snapshot}
                              onChange={(e) => handleSelectItemName(idx, e.target.value)}
                              disabled={!orderForm.business_id || loadingItems}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                fontSize: '13px',
                                background: (!orderForm.business_id || loadingItems) ? '#f8fafc' : '#fff',
                                cursor: (!orderForm.business_id || loadingItems) ? 'not-allowed' : 'pointer'
                              }}
                              required
                            >
                              <option value="">
                                {!orderForm.business_id
                                  ? 'Select Merchant Above First...'
                                  : loadingItems
                                    ? 'Loading merchant items...'
                                    : businessItems.length === 0
                                      ? 'No items found for this merchant'
                                      : 'Select Item...'}
                              </option>
                              {businessItems.map((p, pIdx) => (
                                <option key={p.id || pIdx} value={p.name}>
                                  {p.name} {p.price !== undefined && p.price !== null ? `(₹${p.price})` : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                              style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                              required
                            />
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            <input
                              type="text"
                              placeholder="₹0.00"
                              value={item.unit_price_snapshot !== '' && item.unit_price_snapshot !== null && item.unit_price_snapshot !== undefined ? `₹${item.unit_price_snapshot}` : ''}
                              readOnly
                              style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#f8fafc', cursor: 'not-allowed' }}
                              required
                            />
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            <input
                              type="text"
                              placeholder="0%"
                              value={item.gst_percentage !== '' && item.gst_percentage !== null && item.gst_percentage !== undefined ? `${item.gst_percentage}%` : ''}
                              readOnly
                              style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#f8fafc', cursor: 'not-allowed' }}
                            />
                          </td>
                          <td style={{ padding: '10px 12px', fontWeight: '700', color: '#1e293b' }}>
                            ₹{lineTotal}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(idx)}
                              disabled={items.length === 1}
                              title="Remove Item"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: items.length === 1 ? '#cbd5e1' : '#ef4444',
                                cursor: items.length === 1 ? 'not-allowed' : 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 6: Order Instructions */}
            <div style={{ marginBottom: '28px' }}>
              <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#334155', marginBottom: '14px', borderBottom: '2px solid #f1f5f9', paddingBottom: '6px' }}>
                Order Instruction (Optional)
              </h4>
              <div>
                <input
                  type="text"
                  placeholder="e.g. Make it extra spicy, extra cutlery requested"
                  value={orderForm.order_instruction}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, order_instruction: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>
            </div>

            {/* Form Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                disabled={submittingOrder}
                style={{
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: submittingOrder ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={submittingOrder}
                style={{
                  background: '#F55D00',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: submittingOrder ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 6px rgba(245, 93, 0, 0.25)'
                }}
              >
                {submittingOrder ? <FaSync className="fa-spin" /> : <FaPlus />}
                {submittingOrder ? 'Creating Order...' : 'Submit & Save Order'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* VIEW MODE 3: MANAGE ORDERS PAGE */}
      {viewMode === 'manageOrders' && (
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>

          {/* Sub-Navigation Tabs */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #e2e8f0' }}>
            <button
              type="button"
              onClick={() => setManageTab('counter')}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '700',
                border: 'none',
                borderBottom: manageTab === 'counter' ? '3px solid #F55D00' : '3px solid transparent',
                background: 'transparent',
                color: manageTab === 'counter' ? '#F55D00' : '#64748b',
                cursor: 'pointer',
                marginBottom: '-2px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaReceipt /> Counter Orders (POS)
            </button>
            <button
              type="button"
              onClick={() => {
                setManageTab('online');
                fetchOnlineOrders();
              }}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '700',
                border: 'none',
                borderBottom: manageTab === 'online' ? '3px solid #F55D00' : '3px solid transparent',
                background: 'transparent',
                color: manageTab === 'online' ? '#F55D00' : '#64748b',
                cursor: 'pointer',
                marginBottom: '-2px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaShoppingBag /> Online Delivery Orders
            </button>
          </div>

          {/* Section Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ background: '#fff7ed', color: '#F55D00', padding: '10px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {manageTab === 'counter' ? <FaReceipt style={{ fontSize: '20px' }} /> : <FaShoppingBag style={{ fontSize: '20px' }} />}
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#2A2C41' }}>
                  {manageTab === 'counter' ? 'Business Counter Orders (POS)' : 'Online Delivery Orders'}
                </h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                  {manageTab === 'counter'
                    ? `Manage in-store counter orders recorded for businesses (${counterTotalCount} total orders)`
                    : `Manage online customer delivery orders (${onlineOrders.length} delivery orders)`}
                </p>
              </div>
            </div>

            {/* Filter Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>

              {/* Refresh Button */}
              <button
                onClick={manageTab === 'counter' ? fetchCounterOrders : fetchOnlineOrders}
                disabled={manageTab === 'counter' ? counterLoading : onlineLoading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#2A2C41',
                  color: '#fff',
                  border: 'none',
                  padding: '7px 14px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: (manageTab === 'counter' ? counterLoading : onlineLoading) ? 'not-allowed' : 'pointer'
                }}
              >
                <FaSync className={(manageTab === 'counter' ? counterLoading : onlineLoading) ? "fa-spin" : ""} /> Refresh
              </button>
            </div>
          </div>

          {/* Search bar */}
          <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: '380px' }}>
              <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                type="text"
                placeholder={manageTab === 'counter' ? "Search by Order ID, Merchant, or Customer..." : "Search online delivery orders..."}
                value={manageTab === 'counter' ? counterSearch : onlineSearch}
                onChange={(e) => manageTab === 'counter' ? setCounterSearch(e.target.value) : setOnlineSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { if (manageTab === 'counter') { setCounterPage(1); fetchCounterOrders(); } else { fetchOnlineOrders(); } } }}
                style={{ width: '100%', padding: '8px 12px 8px 36px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }}
              />
            </div>
          </div>

          {(manageTab === 'counter' ? counterError : onlineError) && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>
              {manageTab === 'counter' ? counterError : onlineError}
            </div>
          )}

          {/* Orders Data Table */}
          {(manageTab === 'counter' ? counterLoading : onlineLoading) ? (
            <div style={{ textAlign: 'center', padding: '50px 0', color: '#475569' }}>
              <FaSync className="fa-spin" style={{ fontSize: '28px', color: '#F55D00', marginBottom: '8px' }} />
              <p style={{ fontWeight: '600', fontSize: '14px' }}>
                {manageTab === 'counter' ? 'Loading Business Counter Orders...' : 'Loading Online Delivery Orders...'}
              </p>
            </div>
          ) : (manageTab === 'counter' ? counterOrders : onlineOrders).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
              <FaReceipt style={{ fontSize: '32px', color: '#cbd5e1', marginBottom: '8px' }} />
              <p style={{ fontWeight: '600', fontSize: '14px', margin: 0 }}>
                {manageTab === 'counter' ? 'No counter orders found for the selected criteria.' : 'No online delivery orders found for the selected criteria.'}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                    <th style={{ padding: '12px 14px', fontWeight: '700' }}>Order ID</th>
                    <th style={{ padding: '12px 14px', fontWeight: '700' }}>Order Type</th>
                    <th style={{ padding: '12px 14px', fontWeight: '700' }}>Business Name</th>
                    <th style={{ padding: '12px 14px', fontWeight: '700' }}>Customer</th>
                    {manageTab === 'counter' && <th style={{ padding: '12px 14px', fontWeight: '700' }}>Payment Method</th>}
                    <th style={{ padding: '12px 14px', fontWeight: '700' }}>Payment Status</th>
                    <th style={{ padding: '12px 14px', fontWeight: '700' }}>Order Status</th>
                    <th style={{ padding: '12px 14px', fontWeight: '700' }}>Delivery Partner</th>
                    <th style={{ padding: '12px 14px', fontWeight: '700' }}>Date & Time</th>
                    <th style={{ padding: '12px 14px', fontWeight: '700', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(manageTab === 'counter' ? counterOrders : onlineOrders.slice((onlinePage - 1) * 10, onlinePage * 10)).map((o) => {
                    const statusStr = (o.status || 'ongoing').toLowerCase();
                    const statusColor =
                      statusStr === 'delivered' || statusStr === 'completed'
                        ? { bg: '#dcfce7', color: '#166534' }
                        : statusStr === 'cancelled'
                          ? { bg: '#fee2e2', color: '#991b1b' }
                          : statusStr === 'assigned'
                            ? { bg: '#e0f2fe', color: '#0369a1' }
                            : { bg: '#fff7ed', color: '#c2410c' };

                    const orderTypeVal = (o.order_type || (manageTab === 'counter' ? 'Counter' : 'Delivery')).toLowerCase();

                    return (
                      <tr key={o.id || o.order_id || o.order_number} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 14px', fontWeight: '700', color: '#F55D00', fontSize: '12px' }}>
                          {formatOrderId(o.order_number || o.order_id || o.id)}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            background: orderTypeVal === 'delivery' ? '#e0e7ff' : '#f3e8ff',
                            color: orderTypeVal === 'delivery' ? '#3730a3' : '#6b21a8'
                          }}>
                            {orderTypeVal}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', fontWeight: '600', color: '#2A2C41' }}>
                          {o.business_name || o.business_id || 'Merchant'}
                        </td>
                        <td style={{ padding: '12px 14px', color: '#334155' }}>
                          <div style={{ fontWeight: '600' }}>{o.customer_name || 'Walk-in'}</div>
                          {(o.customer_phone || o.mobileNumber) && <div style={{ fontSize: '11px', color: '#64748b' }}>{o.customer_phone || o.mobileNumber}</div>}
                        </td>
                        {manageTab === 'counter' && (
                          <td style={{ padding: '12px 14px', color: '#334155', textTransform: 'capitalize', fontWeight: '500' }}>
                            {o.payment_method || o.payment_type || 'Cash'}
                          </td>
                        )}
                        <td style={{ padding: '12px 14px', color: '#334155', textTransform: 'capitalize' }}>
                          {(() => {
                            const ps = o.payment_status;
                            if (ps === null || ps === undefined) {
                              return (
                                <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', background: '#f1f5f9', color: '#64748b' }}>
                                  null
                                </span>
                              );
                            }
                            const psLower = String(ps).toLowerCase();
                            const bg = (psLower === 'paid' || psLower === 'success') ? '#dcfce7' : (psLower === 'pending' || psLower === 'cod' ? '#fef3c7' : '#e2e8f0');
                            const color = (psLower === 'paid' || psLower === 'success') ? '#166534' : (psLower === 'pending' || psLower === 'cod' ? '#92400e' : '#334155');
                            return (
                              <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', background: bg, color: color }}>
                                {ps}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: statusColor.bg, color: statusColor.color, textTransform: 'uppercase' }}>
                            {o.status || 'Ongoing'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', color: '#334155' }}>
                          {getDeliveryPartnerDetails(o) ? (
                            <div style={{ fontWeight: '600', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                              <span>🏍️ {getDeliveryPartnerDetails(o)}</span>
                            </div>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '12px', fontStyle: 'italic' }}>Unassigned</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px', color: '#64748b', fontSize: '12px' }}>
                          {o.created_at ? new Date(o.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleViewCounterOrder(o)}
                              style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#2A2C41', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                              <FaEye style={{ color: '#F55D00' }} /> View
                            </button>
                            <button
                              onClick={() => {
                                setStatusUpdateOrder(o);
                                setNewStatusValue(o.status || 'assigned');
                                const partnerId = resolveDeliveryPartnerId(o, deliveryProviders, riders);
                                setSelectedDeliveryPartner(partnerId ? String(partnerId) : '');
                                setUpdateStatusError(null);
                                fetchDeliveryProviders();
                              }}
                              style={{ background: '#F55D00', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                              Update
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination (Counter Orders) */}
              {manageTab === 'counter' && counterTotalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Showing page {counterPage} of {counterTotalPages} ({counterTotalCount} orders)
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      disabled={counterPage <= 1}
                      onClick={() => setCounterPage(prev => Math.max(prev - 1, 1))}
                      style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: counterPage <= 1 ? '#f1f5f9' : '#fff', cursor: counterPage <= 1 ? 'not-allowed' : 'pointer' }}
                    >
                      Previous
                    </button>
                    <button
                      disabled={counterPage >= counterTotalPages}
                      onClick={() => setCounterPage(prev => Math.min(prev + 1, counterTotalPages))}
                      style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: counterPage >= counterTotalPages ? '#f1f5f9' : '#fff', cursor: counterPage >= counterTotalPages ? 'not-allowed' : 'pointer' }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {/* Pagination (Online Delivery Orders) */}
              {manageTab === 'online' && Math.ceil(onlineOrders.length / 10) > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Showing page {onlinePage} of {Math.ceil(onlineOrders.length / 10)} ({onlineOrders.length} orders)
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      disabled={onlinePage <= 1}
                      onClick={() => setOnlinePage(prev => Math.max(prev - 1, 1))}
                      style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: onlinePage <= 1 ? '#f1f5f9' : '#fff', cursor: onlinePage <= 1 ? 'not-allowed' : 'pointer' }}
                    >
                      Previous
                    </button>
                    <button
                      disabled={onlinePage >= Math.ceil(onlineOrders.length / 10)}
                      onClick={() => setOnlinePage(prev => Math.min(prev + 1, Math.ceil(onlineOrders.length / 10)))}
                      style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: onlinePage >= Math.ceil(onlineOrders.length / 10) ? '#f1f5f9' : '#fff', cursor: onlinePage >= Math.ceil(onlineOrders.length / 10) ? 'not-allowed' : 'pointer' }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Status Update Modal */}
          {statusUpdateOrder && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
              <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '420px', padding: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#2A2C41' }}>
                    Update Status: #{statusUpdateOrder.order_id || statusUpdateOrder.id}
                  </h3>
                  <button onClick={() => setStatusUpdateOrder(null)} style={{ background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b' }}>
                    <FaTimes />
                  </button>
                </div>

                {updateStatusError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 12px', borderRadius: '6px', marginBottom: '14px', fontSize: '12px' }}>
                    {updateStatusError}
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '8px' }}>
                    Select New Order Status
                  </label>
                  <select
                    value={newStatusValue}
                    onChange={(e) => setNewStatusValue(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: '600', color: '#2A2C41', background: '#fff' }}
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="preparing">Preparing</option>
                    <option value="assigned">Assigned</option>
                    <option value="ready">Ready</option>
                    <option value="picked_up">Picked Up</option>
                    <option value="travelling">Travelling</option>
                    <option value="out_for_delivery">Out For Delivery</option>
                    <option value="delivered">Delivered</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {newStatusValue === 'assigned' && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '8px' }}>
                      Assign Delivery Partner (Rider) <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <select
                      value={selectedDeliveryPartner}
                      onChange={(e) => {
                        setSelectedDeliveryPartner(e.target.value);
                        if (e.target.value) setUpdateStatusError(null);
                      }}
                      disabled={loadingProviders}
                      required
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: '600', color: '#2A2C41', background: '#fff' }}
                    >
                      <option value="">
                        {loadingProviders ? 'Loading Available Partners...' : '-- Select Delivery Partner / Rider --'}
                      </option>
                      {renderDeliveryPartnerOptions()}
                    </select>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    onClick={() => setStatusUpdateOrder(null)}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#334155', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveCounterStatus}
                    disabled={updatingCounterStatus || (newStatusValue === 'assigned' && !selectedDeliveryPartner)}
                    style={{
                      padding: '8px 20px',
                      borderRadius: '6px',
                      border: 'none',
                      background: '#F55D00',
                      color: '#fff',
                      fontWeight: '600',
                      fontSize: '13px',
                      cursor: (updatingCounterStatus || (newStatusValue === 'assigned' && !selectedDeliveryPartner)) ? 'not-allowed' : 'pointer',
                      opacity: (updatingCounterStatus || (newStatusValue === 'assigned' && !selectedDeliveryPartner)) ? 0.6 : 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {updatingCounterStatus && <FaSync className="fa-spin" />}
                    {updatingCounterStatus ? 'Updating...' : 'Save Status'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Details Modal */}
          {selectedCounterOrder && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
              <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '570px', maxHeight: '90vh', overflowY: 'auto', padding: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', gap: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#2A2C41', lineHeight: '1.4', flex: 1, wordBreak: 'break-word' }}>
                    {(selectedCounterOrder.order_system === 'counter' || manageTab === 'counter') ? 'Counter Order Details' : 'Online Order Details'}:
                    <div style={{ color: '#F55D00', fontSize: '15px', fontWeight: '800', marginTop: '4px', wordBreak: 'break-all' }}>
                      #{selectedCounterOrder.order_number || selectedCounterOrder.order_id || selectedCounterOrder.id}
                    </div>
                  </h3>
                  <button onClick={() => setSelectedCounterOrder(null)} style={{ background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b', padding: '4px', flexShrink: 0 }}>
                    <FaTimes />
                  </button>
                </div>

                {counterDetailLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#475569' }}>
                    <FaSync className="fa-spin" style={{ fontSize: '28px', color: '#F55D00', marginBottom: '10px' }} />
                    <p style={{ fontWeight: '600', fontSize: '14px', margin: 0 }}>Fetching full order details from API...</p>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px', fontSize: '13px', background: '#f8fafc', padding: '12px', borderRadius: '8px' }}>
                      <div><strong>Merchant:</strong> {selectedCounterOrder.business_name || selectedCounterOrder.business_id || 'N/A'}</div>
                      <div>
                        <strong>Customer:</strong> {selectedCounterOrder.customer_name || selectedCounterOrder.customer?.name || 'Walk-in'}
                        {(selectedCounterOrder.customer_phone || selectedCounterOrder.customer?.phone || selectedCounterOrder.mobileNumber) ? (
                          <span> ({selectedCounterOrder.customer_phone || selectedCounterOrder.customer?.phone || selectedCounterOrder.mobileNumber})</span>
                        ) : null}
                      </div>
                      {Boolean(
                        selectedCounterOrder.order_system === 'unrecorded' ||
                        selectedCounterOrder.is_unrecorded ||
                        selectedCounterOrder.unrecorded ||
                        selectedCounterOrder.record_status === 'unrecorded' ||
                        String(selectedCounterOrder.order_number || selectedCounterOrder.order_id || selectedCounterOrder.id || '').startsWith('UNREC_')
                      ) && (selectedCounterOrder.ordered_by_name || selectedCounterOrder.ordered_by_phone) && (
                          <div>
                            <strong>Ordered By:</strong> {selectedCounterOrder.ordered_by_name || 'N/A'}
                            {selectedCounterOrder.ordered_by_phone && (
                              <span> ({selectedCounterOrder.ordered_by_phone})</span>
                            )}
                          </div>
                        )}
                      {(selectedCounterOrder.order_system === 'counter' || manageTab === 'counter') ? (
                        <div><strong>Payment Method:</strong> {selectedCounterOrder.payment_method || selectedCounterOrder.payment_type || 'Cash'}</div>
                      ) : (
                        <div><strong>Payment Source:</strong> {selectedCounterOrder.payment_source || selectedCounterOrder.payment_details?.payment_source || selectedCounterOrder.source || selectedCounterOrder.order_source || 'N/A'}</div>
                      )}
                      <div><strong>Payment Status:</strong> {selectedCounterOrder.payment_status || 'Paid'}</div>
                      <div><strong>Order Status:</strong> {selectedCounterOrder.status || 'Delivered'}</div>
                      <div><strong>Delivery Partner:</strong> {getDeliveryPartnerDetails(selectedCounterOrder) || 'Unassigned'}</div>
                      <div><strong>Date:</strong> {selectedCounterOrder.created_at ? new Date(selectedCounterOrder.created_at).toLocaleString('en-IN') : '-'}</div>
                    </div>

                    <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#2A2C41', marginBottom: '8px' }}>Order Items</h4>
                    {(() => {
                      const orderItems = selectedCounterOrder.items || selectedCounterOrder.order_items || selectedCounterOrder.details?.items || [];
                      return orderItems.length > 0 ? (
                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
                          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#f1f5f9', color: '#475569' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Item</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Qty</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Price</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {orderItems.map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '8px 12px' }}>{item.item_name_snapshot || item.name || item.item_name || item.product_name || 'Item'}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>{item.quantity || 1}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>₹{parseFloat(item.unit_price_snapshot || item.price || item.unit_price || 0).toFixed(2)}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '700' }}>₹{parseFloat(item.total_price || (item.quantity * (item.price || item.unit_price)) || 0).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p style={{ fontSize: '13px', color: '#64748b', fontStyle: 'italic', marginBottom: '16px' }}>No item details attached.</p>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* VIEW MODE 2: FLEET OPERATIONAL DATA GRID */}
      {viewMode === 'grid' && (
        loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
            <FaSync className="fa-spin" style={{ fontSize: '32px', marginBottom: '12px', color: '#F55D00' }} />
            <p>Loading entries and auto-calculating live order counts...</p>
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                    <th style={{ padding: '12px', width: '170px', minWidth: '170px', maxWidth: '170px', position: 'sticky', left: 0, zIndex: 3, background: '#f8fafc', borderRight: '1px solid #e2e8f0', boxSizing: 'border-box' }}>Rider</th>
                    <th style={{ padding: '12px', width: '75px', minWidth: '75px', maxWidth: '75px', position: 'sticky', left: '170px', zIndex: 3, background: '#f8fafc', borderRight: '1px solid #e2e8f0', boxSizing: 'border-box' }}>Att. (P/A)</th>
                    <th style={{ padding: '12px', width: '85px', minWidth: '85px', maxWidth: '85px', position: 'sticky', left: '245px', zIndex: 3, background: '#f8fafc', borderRight: '1px solid #e2e8f0', boxSizing: 'border-box' }}>Total Orders</th>
                    <th style={{ padding: '12px', width: '90px', minWidth: '90px', maxWidth: '90px', position: 'sticky', left: '330px', zIndex: 3, background: '#f8fafc', borderRight: '1px solid #e2e8f0', boxSizing: 'border-box' }}>KM</th>
                    <th style={{ padding: '12px', width: '90px', minWidth: '90px', maxWidth: '90px', position: 'sticky', left: '420px', zIndex: 3, background: '#f8fafc', borderRight: '2px solid #cbd5e1', boxSizing: 'border-box' }}>OT (Hrs)</th>
                    {merchants.map(m => (
                      <th key={m.id} style={{ padding: '12px', minWidth: '130px', background: '#eff6ff', color: '#1e40af', borderRight: '1px solid #dbeafe', textAlign: 'center' }}>
                        {m.name || m.business_name || m.id}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {riders.map(r => {
                    const entry = entries[r.id] || { attendance: 'P', orders: 0, km: '', otHours: '', merchantOrders: {}, notes: '' };
                    const isAbsent = entry.attendance === 'A';
                    const rowBg = isAbsent ? '#fef2f2' : '#ffffff';

                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9', background: rowBg }}>
                        <td style={{ padding: '12px', fontWeight: '600', color: '#1e293b', position: 'sticky', left: 0, zIndex: 2, background: rowBg, borderRight: '1px solid #f1f5f9', width: '170px', minWidth: '170px', maxWidth: '170px', boxSizing: 'border-box' }}>
                          {r.name}
                        </td>

                        <td style={{ padding: '8px', position: 'sticky', left: '170px', zIndex: 2, background: rowBg, borderRight: '1px solid #f1f5f9', width: '75px', minWidth: '75px', maxWidth: '75px', boxSizing: 'border-box' }}>
                          <select
                            disabled={true}
                            value={entry.attendance}
                            style={{
                              padding: '4px 6px',
                              borderRadius: '6px',
                              fontWeight: '600',
                              border: '1px solid #cbd5e1',
                              fontSize: '12px',
                              color: entry.attendance === 'P' ? '#166534' : '#991b1b',
                              background: entry.attendance === 'P' ? '#dcfce7' : '#fee2e2',
                              cursor: 'not-allowed'
                            }}
                          >
                            <option value="P">P</option>
                            <option value="A">A</option>
                          </select>
                        </td>

                        <td style={{ padding: '8px', position: 'sticky', left: '245px', zIndex: 2, background: rowBg, borderRight: '1px solid #f1f5f9', width: '85px', minWidth: '85px', maxWidth: '85px', boxSizing: 'border-box' }}>
                          <input
                            type="number"
                            disabled={true}
                            value={entry.orders}
                            style={{ width: '65px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: '700', background: '#f8fafc', cursor: 'not-allowed' }}
                          />
                        </td>

                        <td style={{ padding: '8px', position: 'sticky', left: '330px', zIndex: 2, background: rowBg, borderRight: '1px solid #f1f5f9', width: '90px', minWidth: '90px', maxWidth: '90px', boxSizing: 'border-box' }}>
                          <input
                            type="number"
                            step="0.1"
                            placeholder="Unlogged"
                            disabled={true}
                            value={entry.km}
                            style={{ width: '70px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#f8fafc', cursor: 'not-allowed' }}
                          />
                        </td>

                        <td style={{ padding: '8px', position: 'sticky', left: '420px', zIndex: 2, background: rowBg, borderRight: '2px solid #cbd5e1', width: '90px', minWidth: '90px', maxWidth: '90px', boxSizing: 'border-box' }}>
                          <input
                            type="number"
                            step="0.5"
                            placeholder="Nil"
                            disabled={true}
                            value={entry.otHours}
                            style={{ width: '70px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#f8fafc', cursor: 'not-allowed' }}
                          />
                        </td>

                        {merchants.map(m => (
                          <td key={m.id} style={{ padding: '8px', background: isAbsent ? '#fee2e2' : '#f8fafc', borderRight: '1px solid #f1f5f9', textAlign: 'center' }}>
                            <input
                              type="number"
                              min="0"
                              disabled={true}
                              value={(entry.merchantOrders && entry.merchantOrders[m.id]) || 0}
                              style={{ width: '70px', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center', background: '#f8fafc', cursor: 'not-allowed' }}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default FleetDataEntry;
